import {
  Action,
  Button,
  Card,
  Checkbox,
  DataStatus,
  EmptyStatus,
  GridPhoto,
  TabsHeader,
} from 'platform/components';
import {AdaptiveImage, Box, Clickable, Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';

import {mergeAll} from 'ramda';

import {useGetServiceAlbumListQuery, useGetServiceAlbumPhotoListQuery} from '@dms/api/sales';
import {ClaraClaimAttachment, useGetMetadaServiceCaseQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {DmsDataGrid, FullScreenModal} from '@dms/shared';

import {
  TestIdProps,
  suffixTestId,
  getFileNameFromUrl,
  getContentTypeFromImageFileSuffix,
} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

import {CLARA_MAX_FILE_SIZE_BYTES} from '../../../../../constants/claraMaxFileSizeBytes';

const DOCUMENTS_TAB = 'DOCUMENTS';
const PHOTOS_TAB = 'PHOTOS';

const PREDEFINED_COLUMNS = ['fileName'] as const;

const ALLOWED_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'] as const;

type FileNameColumnData = {remoteUrl: string; id: string; contentType: string; title: string};

type AllowedContentType = (typeof ALLOWED_CONTENT_TYPES)[number];

type AllowedFileNameColumnData = Omit<FileNameColumnData, 'contentType'> & {
  contentType: AllowedContentType;
};

const PHOTO_FALLBACK_CONTENT_TYPE: AllowedContentType = 'image/jpeg';

function isAllowedContentType(value: string | undefined): value is AllowedContentType {
  return (ALLOWED_CONTENT_TYPES as readonly string[]).includes(value ?? '');
}

export type ClaraSelectedFile = ClaraClaimAttachment;

interface ClaraSelectFromServiceCaseModalProps extends TestIdProps {
  serviceCaseId: string;
  onClose: () => void;
  onFilesSelected: (files: ClaraSelectedFile[]) => void;
}

export function ClaraSelectFromServiceCaseModal(props: ClaraSelectFromServiceCaseModalProps) {
  const [activeTabId, setActiveTabId] = useState<string>(PHOTOS_TAB);
  const [selectedDocuments, setSelectedDocuments] = useState<ClaraSelectedFile[]>([]);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());

  const {
    data: serviceCase,
    isLoading: isServiceCaseLoading,
    isError: isServiceCaseError,
  } = useGetMetadaServiceCaseQuery({serviceCaseId: props.serviceCaseId});

  const {
    data: albums,
    isLoading: isAlbumsLoading,
    isError: isAlbumsError,
  } = useGetServiceAlbumListQuery(
    {vehicleId: serviceCase?.vehicleId ?? '', serviceCaseId: props.serviceCaseId},
    {skip: !serviceCase?.vehicleId}
  );

  const serviceCaseAlbum = albums?.find((album) => album.serviceCaseId === props.serviceCaseId);

  const {
    data: photos,
    isLoading: isPhotosLoading,
    isError: isPhotosError,
  } = useGetServiceAlbumPhotoListQuery(
    {vehicleId: serviceCase?.vehicleId ?? '', serviceAlbumId: serviceCaseAlbum?.id ?? ''},
    {skip: !serviceCase?.vehicleId || !serviceCaseAlbum?.id}
  );

  // Service case album photos mapped to GridPhoto with id = fileId for unified selection tracking
  const serviceCasePhotoItems: GridPhoto[] = (photos ?? []).map((photo) => ({
    id: photo.fileId,
    url: photo.url,
    resizeUrl: photo.url,
    makeUrl: (url, width) => `${url}&width=${width}`,
  }));

  const queryModifier = useCallback(
    (filter: QueryFilterObject) =>
      mergeAll([
        filter,
        {
          targetEntity: 'service-case',
          targetEntityId: props.serviceCaseId,
          contentType: ALLOWED_CONTENT_TYPES,
          fileSize: {from: null, to: CLARA_MAX_FILE_SIZE_BYTES},
        },
      ]),
    [props.serviceCaseId]
  );

  const toggleFileSelection = (id: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllInSection = (ids: string[]) => {
    setSelectedFileIds((prev) => new Set([...prev, ...ids]));
  };

  const handleAddSelected = () => {
    const photoFiles: ClaraSelectedFile[] = (photos ?? [])
      .filter((p) => selectedFileIds.has(p.fileId))
      .map((p): ClaraSelectedFile => {
        const filename = p.url ? getFileNameFromUrl(p.url) : p.fileId;
        const contentType = getContentTypeFromImageFileSuffix(
          filename?.split('.').pop()?.toLowerCase()
        );

        return {
          fileId: p.fileId,
          filename: filename ?? p.fileId,
          contentType: isAllowedContentType(contentType)
            ? contentType
            : PHOTO_FALLBACK_CONTENT_TYPE,
        };
      });

    const allFiles = [...selectedDocuments, ...photoFiles];
    const uniqueFiles = allFiles.filter(
      (file, index) => allFiles.findIndex((f) => f.fileId === file.fileId) === index
    );

    props.onFilesSelected(uniqueFiles);
    props.onClose();
  };

  const totalSelected = selectedDocuments.length + selectedFileIds.size;

  const headline = [i18n.t('entity.claimType.actions.addFilesFromServiceCase'), serviceCase?.number]
    .filter(Boolean)
    .join(' • ');

  return (
    <FullScreenModal
      headline={headline}
      customActions={
        <HStack align="center" spacing={4}>
          <Show when={totalSelected > 0}>
            <Text size="small" color="secondary">
              {i18n.t('entity.claimType.labels.filesSelected', {count: totalSelected})}
            </Text>
          </Show>
          <HStack spacing={2}>
            <Button
              variant="secondary"
              title={i18n.t('general.labels.discard')}
              onClick={props.onClose}
              data-testid={suffixTestId('discard', props)}
            />
            <Button
              title={i18n.t('entity.claimType.actions.addSelected')}
              onClick={handleAddSelected}
              data-testid={suffixTestId('addSelected', props)}
            />
          </HStack>
        </HStack>
      }
      data-testid={props['data-testid']}
    >
      <VStack spacing={0} height="100%">
        <Box backgroundColor="general.white" paddingHorizontal={4} width="100%">
          <TabsHeader
            tabs={[
              {id: DOCUMENTS_TAB, title: i18n.t('general.labels.documents')},
              {id: PHOTOS_TAB, title: i18n.t('entity.photo.labels.photos')},
            ]}
            activeTabId={activeTabId}
            onChange={setActiveTabId}
          />
        </Box>
        <Box backgroundColor="palettes.neutral.20.100" padding={4} flex={1}>
          {match(activeTabId)
            .with(DOCUMENTS_TAB, () => (
              <DmsDataGrid
                gridCode="document-context-list-with-selection"
                predefinedColumns={PREDEFINED_COLUMNS}
                queryModifier={queryModifier}
                onRowSelectionChange={(data) => {
                  const files = data
                    .map((row) => (row.fileName as {value: FileNameColumnData} | undefined)?.value)
                    .filter((item): item is FileNameColumnData => item != null)
                    .filter((item): item is AllowedFileNameColumnData =>
                      isAllowedContentType(item.contentType)
                    );

                  setSelectedDocuments(
                    files.map(
                      (item): ClaraSelectedFile => ({
                        fileId: item.id,
                        filename: item.title,
                        contentType: item.contentType,
                      })
                    )
                  );
                }}
                data-testid={suffixTestId('documents-grid', props)}
              />
            ))
            .with(PHOTOS_TAB, () => (
              <VStack spacing={4}>
                <DataStatus
                  isLoading={isPhotosLoading || isAlbumsLoading || isServiceCaseLoading}
                  isError={isPhotosError || isAlbumsError || isServiceCaseError}
                >
                  <Card
                    key={i18n.t('entity.photo.labels.photos')}
                    title={i18n.t('entity.photo.labels.photos')}
                    actions={
                      serviceCasePhotoItems.length > 0
                        ? ([
                            {
                              type: 'button',
                              variant: 'ghostLink',
                              leftIcon: 'action/done_all',
                              title: i18n.t('general.actions.selectAll'),
                              onClick: () =>
                                selectAllInSection(serviceCasePhotoItems.map((item) => item.id)),
                            },
                          ] satisfies Action[])
                        : []
                    }
                  >
                    <Show when={serviceCasePhotoItems.length === 0}>
                      <EmptyStatus variant="image" />
                    </Show>
                    <Grid columns={4}>
                      {serviceCasePhotoItems.map((item) => {
                        const isSelected = selectedFileIds.has(item.id);
                        return (
                          <Clickable key={item.id} onClick={() => toggleFileSelection(item.id)}>
                            <Box
                              position="relative"
                              border="1px solid"
                              borderColor={
                                isSelected ? 'palettes.blue.60.100' : 'palettes.neutral.30.100'
                              }
                              borderRadius="small"
                              overflow="hidden"
                            >
                              <AdaptiveImage
                                url={item.url}
                                makeUrl={item.makeUrl}
                                ratio="4/3"
                                fit="cover"
                              />
                              <Box position="absolute" bottom={0} right={0} padding={2}>
                                <Checkbox value={isSelected} />
                              </Box>
                            </Box>
                          </Clickable>
                        );
                      })}
                    </Grid>
                  </Card>
                </DataStatus>
              </VStack>
            ))
            .otherwise(() => null)}
        </Box>
      </VStack>
    </FullScreenModal>
  );
}
